import { Injectable } from '@angular/core';
import {
  ExecutionSource,
  CreateQueryRecordMutation,
  CreateQueryRecordMutationVariables,
  QueryStatus,
  CreateQueryRecordResponse,
  GetMetadataQuery,
  GetMetadataQueryVariables,
  GetQueryWithExecutionRecordsQuery,
  GetQueryWithExecutionRecordsQueryVariables,
  QueryWithExecutionRecord,
  SubmitQueryMutation,
  SubmitQueryMutationVariables,
  SubmitQueryResponse,
} from '../../../API';
import { createQueryRecord, submitQuery } from '../../../graphql/mutations';
import { GetMetadata, GetQueryWithExecutionRecords } from '../../../graphql/queries';
import { ApiService } from '../../shared/services/api.service';
import { UserManagementService } from '../../shared/services/user-management.service';
import { EditQueryType, SaveQueryDraftInput } from '../models/market-fan-exploration.model';

@Injectable({
  providedIn: 'root',
})
export class FanExplorationService {
  private editQueryDetails: QueryWithExecutionRecord | null = null;

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
  ) {}

  setEditQueryDetails(details: QueryWithExecutionRecord | null): void {
    this.editQueryDetails = details;
  }

  getEditQueryDetails(): QueryWithExecutionRecord | null {
    return this.editQueryDetails;
  }

  clearEditQueryDetails(): void {
    this.editQueryDetails = null;
  }

  getEditQueryType(record: QueryWithExecutionRecord): EditQueryType {
    if (record.executionSource === ExecutionSource.SEGMENT) {
      return 'SEGMENT';
    }

    if (record.templateId?.trim()) {
      return 'STANDARD';
    }

    return 'CUSTOM';
  }

  async fetchEditQueryDetails(queryId: string, executionId: string): Promise<QueryWithExecutionRecord | null> {
    if (!queryId?.trim() || !executionId?.trim()) {
      return null;
    }

    const orgId = this.userManagementService.getOrgId();

    if (!orgId) {
      throw new Error('Unable to load query details: orgId is missing for the current user.');
    }

    const response = await this.apiService.query<
      GetQueryWithExecutionRecordsQuery,
      GetQueryWithExecutionRecordsQueryVariables
    >(GetQueryWithExecutionRecords, {
      executionId: executionId.trim(),
      orgId,
      queryId: queryId.trim(),
    });

    const record = response.data?.GetQueryWithExecutionRecords ?? null;
    this.editQueryDetails = record;

    return record;
  }

  async getOrFetchEditQueryDetails(queryId: string, executionId: string): Promise<QueryWithExecutionRecord | null> {
    if (
      this.editQueryDetails?.queryId?.trim() === queryId.trim() &&
      this.editQueryDetails?.executionId?.trim() === executionId.trim()
    ) {
      return this.editQueryDetails;
    }

    return this.fetchEditQueryDetails(queryId, executionId);
  }

  async saveDraft(input: SaveQueryDraftInput): Promise<CreateQueryRecordResponse | null> {
    const orgId = this.userManagementService.getOrgId();
    const createdBy = this.userManagementService.getEmail();

    if (!orgId || !createdBy) {
      throw new Error('User information is missing.');
    }

    const response = await this.apiService.query<CreateQueryRecordMutation, CreateQueryRecordMutationVariables>(
      createQueryRecord,
      {
        queryRecordInput: {
          selectedFilterConfig: input.selectedFilterConfig,
          createdBy,
          description: input.description?.trim() || '',
          executionSource: input.executionSource,
          executionId: input.executionId?.trim() || undefined,
          name: input.name.trim(),
          orgId,
          query: input.query.trim(),
          queryId: input.queryId?.trim() || undefined,
          queryType: input.queryType,
          status: QueryStatus.DRAFT,
          templateId: input.templateId,
          validationLogs: input.validationLogs,
          validationStatus: input.validationStatus ?? undefined,
        },
      },
    );

    return response.data?.createQueryRecord ?? null;
  }

  async submitQuery(input: any): Promise<SubmitQueryResponse | null> {
    const orgId = this.userManagementService.getOrgId();
    const lastModifiedBy = this.userManagementService.getEmail();
    const { queryId, executionId } = input;

    if (!orgId || !lastModifiedBy) {
      throw new Error('User information is missing.');
    }

    const response = await this.apiService.query<SubmitQueryMutation, SubmitQueryMutationVariables>(submitQuery, {
      input: {
        orgId,
        executionId,
        queryId,
        lastModifiedBy,
      },
    });

    return response.data?.submitQuery ?? null;
  }

  async getMetadata(source: string): Promise<GetMetadataQuery['GetMetadata'] | null> {
    if (!source?.trim()) {
      throw new Error('Organization information is missing.');
    }

    const response = await this.apiService.query<GetMetadataQuery, GetMetadataQueryVariables>(GetMetadata, {
      source: source.trim(),
    });

    return response.data?.GetMetadata ?? null;
  }
}
